import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useAuthStore } from '@/store/auth';
import {
    ActiveRoleAssignmentResponse,
    getAllRoleAssignments,
    RoleAssignmentDetailed,
    type RoleAssignmentsContainer,
} from '@pillar-portal/api-types';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

interface OrganizationRoleSwitcherProps {
    user: RoleAssignmentsContainer;
}

interface RoleSwitchLogger {
    error: (message: string, err: unknown) => void;
}

function requireRoleAssignmentUuid(
    roleAssignment: RoleAssignmentDetailed
): RoleAssignmentDetailed & { role_assignment_uuid: string } {
    if (roleAssignment.role_assignment_uuid === undefined) {
        throw new Error('role_assignment_uuid is required for role assignment operations');
    }
    return {
        ...roleAssignment,
        role_assignment_uuid: roleAssignment.role_assignment_uuid,
    };
}

export function resetReactQueryAfterRoleSwitch(
    queryClient: QueryClient,
    log: RoleSwitchLogger
): void {
    // Switching active role assignment changes the authorization context.
    // Clear/reset React Query state to force all active views to refetch using the new identity.
    runInBackground(queryClient.cancelQueries(), (err) => {
        log.error('Error cancelling queries after role switch', err);
    });
    runInBackground(queryClient.resetQueries({ predicate: () => true }), (err) => {
        log.error('Error resetting queries after role switch', err);
    });
}

export function OrganizationRoleSwitcher({ user }: OrganizationRoleSwitcherProps) {
    const log = createComponentLogger('OrganizationRoleSwitcher');
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const setActiveRoleAssignmentId: (activeRoleAssignmentId: number | null) => void = useAuthStore(
        (state) => state.setActiveRoleAssignmentId
    );

    const setActiveRoleAssignmentMutation = useMutation({
        mutationFn: authApi.setActiveRoleAssignment,
        onSuccess: () => {
            // Avoid carrying stale pkey state while the active-role query refetches.
            setActiveRoleAssignmentId(null);
            resetReactQueryAfterRoleSwitch(queryClient, log);
        },
    });

    const roleAssignments: RoleAssignmentDetailed[] = getAllRoleAssignments(user);

    // Fetch active role assignment from current session
    const { data: activeRoleAssignmentData } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!user,
        staleTime: 30000,
    });
    useEffect(() => {
        const nextActiveRoleAssignmentId: number | null =
            roleAssignments.find(
                (roleAssignment) =>
                    roleAssignment.role_assignment_uuid ===
                    activeRoleAssignmentData?.role_assignment_uuid
            )?.role_assignment_id ?? null;
        if (activeRoleAssignmentId === nextActiveRoleAssignmentId) {
            return;
        }
        setActiveRoleAssignmentId(nextActiveRoleAssignmentId);
    }, [
        activeRoleAssignmentData?.role_assignment_uuid,
        activeRoleAssignmentId,
        roleAssignments,
        setActiveRoleAssignmentId,
    ]);

    // Find the current active role assignment from the fetched data
    const activeRoleAssignment =
        activeRoleAssignmentData !== null && activeRoleAssignmentData !== undefined
            ? (roleAssignments.find(
                  (roleAssignment) =>
                      roleAssignment.role_assignment_uuid ===
                      activeRoleAssignmentData.role_assignment_uuid
              ) ?? null)
            : null;

    // If user has no roles (or only one), no switching UI is needed.
    if (roleAssignments.length <= 1) {
        return (
            <div className="text-xs text-gray-500 truncate">
                {roleAssignments[0]?.role ?? 'No roles assigned'}
            </div>
        );
    }

    const roleAssignmentOptions = roleAssignments.map((roleAssignment) =>
        requireRoleAssignmentUuid(roleAssignment)
    );

    const handleValueChange = (value: string) => {
        setActiveRoleAssignmentMutation.mutate(value);
    };

    const formatRoleAssignment = (roleAssignment: RoleAssignmentDetailed) => {
        if (roleAssignment.organization_id === null) {
            return `${roleAssignment.role} (internal)`;
        }
        const orgName =
            roleAssignment.organization_name || `Org #${roleAssignment.organization_id}`;
        return `${roleAssignment.role} @ ${orgName}`;
    };

    if (activeRoleAssignment && activeRoleAssignment.role_assignment_uuid === undefined) {
        throw new Error('role_assignment_uuid is required for role assignment operations');
    }
    const currentValue: string = activeRoleAssignment?.role_assignment_uuid ?? '';

    return (
        <Select
            value={currentValue}
            onValueChange={handleValueChange}
            disabled={setActiveRoleAssignmentMutation.isPending}
        >
            <SelectTrigger className="h-auto py-1 px-2 text-xs bg-gray-50 border-gray-200">
                <SelectValue placeholder="Select role">
                    {activeRoleAssignment ? (
                        <div className="text-left">
                            <div className="font-medium text-gray-900 truncate">
                                {activeRoleAssignment.organization_id === null
                                    ? 'Internal'
                                    : activeRoleAssignment.organization_name ||
                                      `Org #${activeRoleAssignment.organization_id}`}
                            </div>
                            <div className="text-gray-500 truncate">
                                {activeRoleAssignment.role}
                            </div>
                        </div>
                    ) : (
                        <span className="text-gray-500">No active role</span>
                    )}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {roleAssignmentOptions.map((roleAssignment) => (
                    <SelectItem
                        key={roleAssignment.role_assignment_id}
                        value={roleAssignment.role_assignment_uuid}
                    >
                        {formatRoleAssignment(roleAssignment)}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
